import { useMemo, useRef, useState } from 'react';
import { Icon } from '../chrome/Icon';
import { AvatarButton } from '../chrome/Avatar';
import { NotificationBell } from '../chrome/NotificationsSheet';
import {
  useBabyMilestones,
  useLeaps,
  useToday,
  useUpdateMilestoneStatus,
  useActivitySuggestions,
  useSetActivityState,
  useMe,
  useBabyAccess,
  useGrowth,
  useStartTrial,
  useBabyInvites,
  useQuestionnaires,
} from '../../data/queries';
import { rowToLeap } from '../../data/derive';
import { babyCaps } from '../../data/permissions';
import { fmtRange } from '../shared/statusMeta';
import { MissingText, useIsUntranslated } from '../shared/MissingText';
import { useEncryption } from '../../crypto/EncryptionProvider';
import { useMediaBlob, useCatalogOrBlob } from '../media/useMediaBlob';
import { useT } from '../../i18n';
import { useAuth } from '../../auth/AuthProvider';
import type { TabId } from '../chrome/AppShell';
import type { BabyMilestoneRow, MediaRow, ActivitySummary, QuestionnaireItem } from '../../data/dto';
import type { Baby } from '../../data/types';
import { ACTIVITY_CATEGORY_META } from '../activities/activityMeta';
import { activityFitsAge } from '../activities/activityAge';
import { QuestionnaireSheet } from '../questionnaires/QuestionnaireSheet';

interface Props {
  baby: Baby;
  onOpenProfile: () => void;
  onOpenNotifications?: () => void;
  onOpenDetail: (id: string) => void;
  onOpenDashboard: () => void;
  onOpenActivity: (activityId: number) => void;
  onOpenActivities: () => void;
  onAddPhotos?: () => void;
  onSwitchTab?: (tab: TabId) => void;
}

/** Short display label for a milestone's group_key. */
const CAT_LABEL: Record<string, string> = {
  motor_gross:      'Motor',
  motor_fine:       'Motor',
  communication:    'Communication',
  social_emotional: 'Social',
  cognitive:        'Cognitive',

  'Motor - Gross':      'Motor',
  'Motor - Fine':       'Motor',
  'Communication':      'Communication',
  'Social & Emotional': 'Social',
  'Cognitive':          'Cognitive',
  'Boundary Stone':     'Boundary Stone',
  'Language':           'Language',
  'Nutrition':          'Nutrition',
  'Self-Care':          'Self-Care',
  'Sleep':              'Sleep',
  'Teeth':              'Teeth',
};

const CAT_ICONS: Record<string, { icon: string; color: string }> = {
  motor_gross:          { icon: 'play',    color: '#D98E5A' },
  motor_fine:           { icon: 'play',    color: '#D98E5A' },
  communication:        { icon: 'sing',    color: '#A579B0' },
  social_emotional:     { icon: 'explore', color: '#5E92B0' },
  cognitive:            { icon: 'read',    color: '#6F9E6E' },

  'Motor - Gross':      { icon: 'play',    color: '#D98E5A' },
  'Motor - Fine':       { icon: 'play',    color: '#D98E5A' },
  'Communication':      { icon: 'sing',    color: '#A579B0' },
  'Social & Emotional': { icon: 'explore', color: '#5E92B0' },
  'Cognitive':          { icon: 'read',    color: '#6F9E6E' },
  'Boundary Stone':     { icon: 'journey', color: '#888888' },
  'Language':           { icon: 'sing',    color: '#A579B0' },
  'Nutrition':          { icon: 'plus',    color: '#6F9E6E' },
  'Self-Care':          { icon: 'plus',    color: '#5E92B0' },
  'Sleep':              { icon: 'plus',    color: '#5E92B0' },
  'Teeth':              { icon: 'plus',    color: '#D98E5A' },
};

/**
 * Today screen — the app's home view.
 */
export function Today({
  baby,
  onOpenProfile,
  onOpenNotifications,
  onOpenDetail,
  onOpenDashboard,
  onOpenActivity,
  onOpenActivities,
  onSwitchTab,
}: Props) {
  const msQ      = useBabyMilestones(baby.id ?? null);
  const leapsQ   = useLeaps();
  const todayQ   = useToday(baby.id ?? null);
  const isUntranslated = useIsUntranslated();
  const suggestQ = useActivitySuggestions(baby.id ?? null);
  const updateStatus = useUpdateMilestoneStatus();
  const caps = babyCaps(baby);
  const t = useT();

  const me = useMe();
  const accessQ = useBabyAccess(baby.id ?? null);
  const invitesQ = useBabyInvites(baby.id ?? null);
  const growthQ = useGrowth(baby.id ?? null);
  const questionnairesQ = useQuestionnaires(baby.id ?? null);
  const auth = useAuth();
  const startTrial = useStartTrial();

  const [openQuestionnaire, setOpenQuestionnaire] = useState<QuestionnaireItem | null>(null);

  const [dismissedTaskIds, setDismissedTaskIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('babysteps.dismissed_next_steps');
      return stored ? new Set(JSON.parse(stored)) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  });

  const handleDismissTask = (taskId: string) => {
    setDismissedTaskIds((prev) => {
      const updated = new Set(prev);
      updated.add(taskId);
      try {
        localStorage.setItem('babysteps.dismissed_next_steps', JSON.stringify(Array.from(updated)));
      } catch (e) {
        console.error('Failed to save dismissed tasks', e);
      }
      return updated;
    });
  };

  const [taskStates, setTaskStates] = useState<Record<string, 'idle' | 'loading' | 'success' | 'error'>>({});

  const handleTaskAction = (task: any) => {
    const taskId = task.id;
    setTaskStates((prev) => ({ ...prev, [taskId]: 'loading' }));

    switch (task.action) {
      case 'verify_email':
        auth
          .resendVerificationEmail()
          .then(() => {
            setTaskStates((prev) => ({ ...prev, [taskId]: 'success' }));
            setTimeout(() => {
              setTaskStates((prev) => ({ ...prev, [taskId]: 'idle' }));
            }, 3000);
          })
          .catch((e) => {
            console.error(e);
            setTaskStates((prev) => ({ ...prev, [taskId]: 'error' }));
            setTimeout(() => {
              setTaskStates((prev) => ({ ...prev, [taskId]: 'idle' }));
            }, 3000);
          });
        break;

      case 'start_trial':
        startTrial.mutate(undefined, {
          onSuccess: () => {
            setTaskStates((prev) => ({ ...prev, [taskId]: 'success' }));
          },
          onError: (e) => {
            console.error(e);
            setTaskStates((prev) => ({ ...prev, [taskId]: 'error' }));
            setTimeout(() => {
              setTaskStates((prev) => ({ ...prev, [taskId]: 'idle' }));
            }, 3000);
          },
        });
        break;

      case 'share_baby':
        setTaskStates((prev) => ({ ...prev, [taskId]: 'success' }));
        onOpenProfile();
        setTimeout(() => {
          setTaskStates((prev) => ({ ...prev, [taskId]: 'idle' }));
        }, 1000);
        break;

      case 'enter_growth':
        setTaskStates((prev) => ({ ...prev, [taskId]: 'success' }));
        if (onSwitchTab) {
          onSwitchTab('growth');
        }
        setTimeout(() => {
          setTaskStates((prev) => ({ ...prev, [taskId]: 'idle' }));
        }, 1000);
        break;

      case 'open_questionnaire':
        if (task.questionnaire) setOpenQuestionnaire(task.questionnaire);
        setTaskStates((prev) => ({ ...prev, [taskId]: 'idle' }));
        break;
    }
  };

  const actionableTasks = useMemo(() => {
    const list: any[] = [];
    if (!me.data) return list;

    const user = me.data.user;
    const entitlements = me.data.entitlements;

    // 1. Verify Email (if unverified)
    if (user.email_verified_at === null) {
      const createdTime = new Date(user.created_at + ' UTC').getTime();
      const daysOld = (Date.now() - createdTime) / (1000 * 60 * 60 * 24);
      const remainingDays = Math.max(0, Math.ceil(7 - daysOld));
      list.push({
        id: 'verify_email',
        title: t('tasks.verifyEmail.title', 'Confirm your email'),
        description: remainingDays > 0
          ? t(
              'tasks.verifyEmail.desc',
              'Please confirm your email address. You have {days} days remaining.'
            ).replace('{days}', String(remainingDays))
          : t(
              'tasks.verifyEmail.expiredDesc',
              'Please confirm your email address to keep access to your account.'
            ),
        cta: t('tasks.verifyEmail.cta', 'Resend Link'),
        icon: 'mail',
        color: 'var(--color-accent)',
        action: 'verify_email',
      });
    }

    // 2. Subscription Trial (if eligible and not active pro)
    if (entitlements?.trial?.eligible && !user.is_pro) {
      list.push({
        id: 'start_trial',
        title: t('tasks.startTrial.title', 'Try Family free for 2 weeks'),
        description: t(
          'tasks.startTrial.desc',
          'Unlock AI activities, milestones, and unlimited photo storage.'
        ),
        cta: t('tasks.startTrial.cta', 'Start Free Trial'),
        icon: 'trophy',
        color: '#D4A845',
        action: 'start_trial',
      });
    }

    // 3. Share with another person (if baby owner, co-parents count is 1, and no pending invites)
    const userRole = baby.role;
    const isOwner = userRole === 'owner';
    const coParentsCount = accessQ.data?.length ?? 0;
    const pendingInvitesCount = invitesQ.data?.length ?? 0;
    if (isOwner && coParentsCount <= 1 && pendingInvitesCount === 0 && accessQ.isSuccess && invitesQ.isSuccess) {
      list.push({
        id: 'share_baby',
        title: t('tasks.shareBaby.title', 'Invite a co-parent'),
        description: t(
          'tasks.shareBaby.desc',
          "Share {name}'s profile with a partner to track milestones together."
        ).replace('{name}', baby.name),
        cta: t('tasks.shareBaby.cta', 'Invite Now'),
        icon: 'profile',
        color: '#5E92B0',
        action: 'share_baby',
      });
    }

    // 4. Growth data reminder (if no records exist, or the last is >= 1 month ago)
    if (growthQ.isSuccess) {
      const records = growthQ.data ?? [];
      let shouldShowGrowthTask = false;
      if (records.length === 0) {
        shouldShowGrowthTask = true;
      } else {
        const latestRecord = records.reduce((latest: any, current: any) => {
          const latestTime = new Date(latest.recorded_at + 'T00:00:00Z').getTime();
          const currentTime = new Date(current.recorded_at + 'T00:00:00Z').getTime();
          return currentTime > latestTime ? current : latest;
        }, records[0]);

        const latestDate = new Date(latestRecord.recorded_at + 'T00:00:00Z').getTime();
        const oneMonthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
        if (latestDate <= oneMonthAgo) {
          shouldShowGrowthTask = true;
        }
      }
      if (shouldShowGrowthTask) {
        list.push({
          id: 'enter_growth',
          title: t('tasks.enterGrowth.title', 'Enter growth data'),
          description:
            records.length === 0
              ? t(
                  'tasks.enterGrowth.emptyDesc',
                  'Add weight, height, or head size to see growth curves.'
                )
              : t(
                  'tasks.enterGrowth.oldDesc',
                  "It's been a while since your last growth record. Let's check in!"
                ),
          cta: t('tasks.enterGrowth.cta', 'Add Measurement'),
          icon: 'growth',
          color: '#6F9E6E',
          action: 'enter_growth',
        });
      }
    }

    // 5. Questionnaires whose trigger fell within the last 7 days and aren't done.
    for (const q of questionnairesQ.data?.questionnaires ?? []) {
      if (!q.show_in_next_steps) continue;
      list.push({
        id: `questionnaire_${q.code}`,
        title: t('questionnaire.nextSteps.title', 'Capture a memory'),
        description: t('questionnaire.nextSteps.desc', 'Take a moment to reflect on {title}.').replace('{title}', q.title),
        cta: t('questionnaire.nextSteps.cta', 'Start'),
        icon: 'edit',
        color: 'var(--color-accent)',
        action: 'open_questionnaire',
        questionnaire: q,
      });
    }

    return list.filter((task) => !dismissedTaskIds.has(task.id));
  }, [me.data, accessQ.data, accessQ.isSuccess, invitesQ.data, invitesQ.isSuccess, growthQ.data, growthQ.isSuccess, questionnairesQ.data, baby, t, dismissedTaskIds]);

  const ageWeeks = baby.ageWeeks;
  const rows: BabyMilestoneRow[] = msQ.data?.milestones ?? [];

  const leaps = useMemo(() => (leapsQ.data ?? []).map(rowToLeap), [leapsQ.data]);

  // ── 2. "This age" statistics ────────────────────────────────────────
  const expected = useMemo(
    () => rows.filter((r) => r.median_achieved_weeks <= ageWeeks),
    [rows, ageWeeks],
  );
  const expectedDone = expected.filter((r) => r.status === 'achieved').length;
  const expectedTotal = expected.length;

  // ── 3. Working on ───────────────────────────────────────────────────
  const workingOn = useMemo(
    () =>
      rows
        .filter((r) => r.status === 'in_progress')
        .sort((a, b) => a.median_achieved_weeks - b.median_achieved_weeks),
    [rows],
  );

  // ── 4. Up next — relevant open milestones ───────────────────────────
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());
  const relevant = useMemo(
    () =>
      rows
        .filter(
          (r) =>
            r.status === 'not_started' &&
            r.avg_start_weeks <= ageWeeks &&
            !dismissed.has(r.milestone_id),
        )
        .sort((a, b) => a.median_achieved_weeks - b.median_achieved_weeks),
    [rows, ageWeeks, dismissed],
  );

  // ── Activities suggestions ──────────────────────────────────────────
  // Keep only activities whose recommended band covers the baby's current age.
  // The server may include milestone-linked ideas via the upcoming-milestone
  // window; this trims those so Today shows strictly age-appropriate activities.
  const todaySuggestions = useMemo(
    () => (suggestQ.data?.suggestions ?? []).filter((a) => activityFitsAge(a, baby.ageWeeks)),
    [suggestQ.data, baby.ageWeeks],
  );
  const joke   = todayQ.data?.joke ?? null;
  const smile  = joke?.content ?? null;

  // ── Mark-in-progress for the Up next carousel ───────────────────────
  const [pendingId, setPendingId] = useState<number | null>(null);
  const markInProgress = (milestoneId: number) => {
    if (!baby.id) return;
    setPendingId(milestoneId);
    updateStatus.mutate(
      { babyId: baby.id, milestoneId, status: 'in_progress', achieved_date: null },
      { onSettled: () => setPendingId(null) },
    );
  };
  const dismiss = (milestoneId: number) =>
    setDismissed((prev) => new Set(prev).add(milestoneId));

  // ── Grid Images calculation (6 buckets from 104 weeks) ──────────────
  const gridImages = useMemo(() => {
    const raw = todayQ.data?.grid_images ?? [];
    const padded = [...raw];
    while (padded.length < 6) padded.push(null);
    return padded.slice(0, 6);
  }, [todayQ.data?.grid_images]);

  return (
    <div className="relative -mx-5 nav:-mx-8 -mt-4 nav:-mt-10 pb-6 bg-bg min-h-dvh">
      {/* ── Top Grid Hero Area ────────────────────────────────────────── */}
      <div className="relative h-60 overflow-hidden bg-tint">
        <ImageGrid images={gridImages} />

        {/* Overlay progress circle */}
        <div className="absolute right-6 top-1/2 -translate-y-1/2 z-10">
          <ProgressCircle
            done={expectedDone}
            total={expectedTotal}
            onClick={onOpenDashboard}
          />
        </div>

        {/* 60% overlay gradient */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, transparent 55%, var(--color-bg) 100%)' }}
        />
      </div>

      {/* ── Body Content (Negative Margin Overlays Hero) ──────────────── */}
      <div className="relative z-2 px-5 nav:px-8 -mt-10 flex flex-col gap-5">
        {/* Header */}
        <header className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink-3">
              {t('today.title', 'Today')}
            </p>
            <h1 className="mt-1 font-serif text-[26px] nav:text-3xl leading-tight tracking-tight text-ink line-clamp-2">
              {t('today.headline', '{name} is currently in week {week}')
                .replace('{name}', baby.name)
                .replace('{week}', String(baby.ageWeeks))}
            </h1>
          </div>
          <div className="flex items-center gap-1">
            {onOpenNotifications && <NotificationBell babyId={baby.id ?? null} onOpen={onOpenNotifications} />}
            <div className="nav:hidden">
              <AvatarButton size={40} baby={baby} onOpenProfile={onOpenProfile} />
            </div>
          </div>
        </header>

        {/* ── Actionable Tasks (Next Steps) ───────────────────────────── */}
        {actionableTasks.length > 0 && (
          <section className="mb-2">
            <SectionEyebrow
              label={t('today.tasks.title', 'Next Steps')}
              meta={actionableTasks.length > 1 ? t('today.tasks.count', '{count} tasks').replace('{count}', String(actionableTasks.length)) : undefined}
            />
            <div className="scrollbar-none flex gap-4 overflow-x-auto snap-x snap-mandatory pb-1 px-1">
              {actionableTasks.map((task) => {
                const state = taskStates[task.id] || 'idle';
                return (
                  <div
                    key={task.id}
                    className="w-[85%] nav:w-[350px] shrink-0 snap-start rounded-2xl border border-line bg-surface p-4 flex flex-col justify-between shadow-xs relative overflow-hidden group transition-all"
                  >
                    {/* Color accent bar on the left */}
                    <div
                      className="absolute top-0 left-0 bottom-0 w-1.5"
                      style={{ backgroundColor: task.color }}
                    />
                    <div className="pl-2.5">
                      <div className="flex items-center gap-2 mb-1.5 pr-6">
                        <span
                          className="flex items-center justify-center rounded-lg p-1.5"
                          style={{ backgroundColor: `${task.color}15`, color: task.color }}
                        >
                          <Icon name={task.icon} size={16} color={task.color} />
                        </span>
                        <h4 className="font-serif text-[16px] leading-tight tracking-tight text-ink font-semibold">
                          {task.title}
                        </h4>
                      </div>
                      <p className="text-[13px] leading-snug text-ink-2 mb-3">
                        {task.description}
                      </p>
                    </div>
                    {/* Dismiss Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDismissTask(task.id);
                      }}
                      className="absolute top-3.5 right-3.5 text-[11px] font-bold text-ink-3 hover:text-ink transition-colors p-1 cursor-pointer z-10"
                      aria-label={t('common.dismiss', 'Dismiss')}
                    >
                      ✕
                    </button>
                    <div className="pl-2.5 flex items-center justify-end">
                      <button
                        onClick={() => handleTaskAction(task)}
                        disabled={state === 'loading' || state === 'success'}
                        className="inline-flex items-center justify-center rounded-full bg-ink px-4 py-1.5 text-[12px] font-semibold text-bg active:scale-95 transition-all disabled:opacity-50 cursor-pointer min-h-8 min-w-[100px]"
                      >
                        {state === 'loading' ? (
                          <span className="w-4 h-4 border-2 border-bg border-t-transparent animate-spin rounded-full" />
                        ) : state === 'success' ? (
                          task.id === 'verify_email' ? t('tasks.verifyEmail.sentCTA', 'Sent!') : t('common.done', 'Done!')
                        ) : (
                          task.cta
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Working on (Horizontal Scroll-X) ────────────────────────── */}
        <section>
          <SectionEyebrow
            label={t('today.workingOn.title', 'Working on')}
            meta={`${workingOn.length} ${workingOn.length === 1 ? t('today.workingOn.milestone', 'milestone') : t('today.workingOn.milestones', 'milestones')}`}
          />
          {workingOn.length === 0 ? (
            <div className="rounded-2xl border border-line bg-surface p-4 text-[13px] italic text-ink-3">
              {t('today.workingOn.none', 'Nothing flagged right now — rest counts too.')}
            </div>
          ) : (
            <div className="scrollbar-none flex gap-4 overflow-x-auto snap-x snap-mandatory pb-1 px-1">
              {workingOn.map((m) => (
                <WorkingOnCard
                  key={m.milestone_id}
                  milestone={m}
                  onOpen={() => onOpenDetail(String(m.milestone_id))}
                />
              ))}
            </div>
          )}
        </section>

        {/* ── Up next (Redesigned Milestone List Card Layout) ─────────── */}
        {relevant.length > 0 && (
          <section>
            <SectionEyebrow
              label={t('today.upNext.title', 'Up next')}
              meta={t('today.upNext.meta', '{count} to watch for').replace('{count}', String(relevant.length))}
            />
            <UpNextCarousel
              items={relevant}
              dobIso={baby.dobIso}
              pendingId={pendingId}
              onOpen={(id) => onOpenDetail(String(id))}
              onSeen={caps.canWrite ? markInProgress : undefined}
              onDismiss={dismiss}
            />
          </section>
        )}

        {/* ── Activities (Horizontal Scroll-X) ────────────────────────── */}
        {todaySuggestions.length > 0 && (
          <section>
            <div className="mb-2.5 flex items-baseline justify-between px-1">
              <h3 className="font-serif text-[20px] italic leading-tight tracking-tight text-ink">
                {t('today.activities.title', 'Activities')}
              </h3>
              <button
                onClick={onOpenActivities}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-ink-2 active:scale-95 transition-transform"
              >
                {t('common.showAll', 'Show all')}
                <Icon name="chevron" size={12} color="var(--color-ink-2)" />
              </button>
            </div>
            
            <div className="scrollbar-none flex gap-4 overflow-x-auto snap-x snap-mandatory pb-1 px-1">
              {todaySuggestions.slice(0, 5).map((a) => (
                <ActivityAirbnbCard
                  key={a.id}
                  activity={a}
                  onClick={() => onOpenActivity(a.id)}
                  babyId={baby.id}
                />
              ))}
              {todaySuggestions.length >= 5 && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={onOpenActivities}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onOpenActivities();
                    }
                  }}
                  className="w-[45%] shrink-0 snap-start flex flex-col text-left group cursor-pointer outline-none"
                >
                  <div
                    className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden border border-dashed border-line flex flex-col items-center justify-center gap-1.5 bg-tint/5 border-tint/30 transition-all group-active:scale-95 duration-200"
                  >
                    <div
                      className="flex w-10 h-10 items-center justify-center rounded-full bg-tint/10 text-accent transition-transform group-hover:scale-110"
                    >
                      <Icon name="arrow" size={16} color="var(--color-accent)" />
                    </div>
                    <span className="text-[12px] font-semibold text-ink-2 mt-1">
                      {t('common.showAll', 'Show all')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ── A small smile (Joke Section) ────────────────────────────── */}
        {smile && (
          <section className="mt-2">
            <h3 className="font-serif text-[20px] italic leading-tight tracking-tight text-ink mb-2 px-1">
              {t('today.smile.title', 'A small smile')}
            </h3>
            <div className="rounded-2xl border border-line bg-surface p-4">
              {isUntranslated(joke) ? (
                <MissingText />
              ) : (
                <p className="font-serif text-[17px] italic leading-snug text-ink text-pretty">
                  &ldquo;{smile}&rdquo;
                </p>
              )}
            </div>
          </section>
        )}

        {/* ── Growth Card (Similar to iOS) ────────────────────────────── */}
        <section className="mt-2">
          <button
            onClick={() => onSwitchTab?.('growth')}
            className="w-full rounded-2xl border border-line bg-surface p-4 flex items-center justify-between text-left transition-all active:scale-[0.98] hover:border-accent/40 cursor-pointer outline-none group"
          >
            <div className="flex items-center gap-3">
              <span
                className="flex items-center justify-center rounded-xl w-11 h-11 shrink-0"
                style={{
                  backgroundColor: 'var(--color-accent-soft)',
                  color: 'var(--color-accent)',
                }}
              >
                <Icon name="growth" size={20} color="var(--color-accent)" />
              </span>
              <div>
                <h4 className="font-serif text-[16px] leading-tight tracking-tight text-ink font-semibold group-hover:text-accent transition-colors">
                  {t('growth.title', 'Growth')}
                </h4>
                <p className="text-[12px] leading-snug text-ink-3 mt-1">
                  {t('today.growth.desc', 'Weight, height & head curves with percentiles')}
                </p>
              </div>
            </div>
            <Icon name="chevron" size={12} color="var(--color-ink-3)" />
          </button>
        </section>
      </div>

      {openQuestionnaire && baby.id && (
        <QuestionnaireSheet
          open
          baby={baby}
          item={openQuestionnaire}
          questions={questionnairesQ.data?.questions[openQuestionnaire.question_set] ?? []}
          onClose={() => setOpenQuestionnaire(null)}
        />
      )}
    </div>
  );
}

/* ── Local Subcomponents & Helpers ───────────────────────────────────── */

function SectionEyebrow({ label, meta }: { label: string; meta?: string }) {
  return (
    <div className="mb-2.5 flex items-baseline justify-between px-1">
      <h3 className="font-serif text-[20px] italic leading-tight tracking-tight text-ink">{label}</h3>
      {meta && <span className="text-[11.5px] font-semibold text-ink-3">{meta}</span>}
    </div>
  );
}

function ImageGrid({ images }: { images: (MediaRow | null)[] }) {
  return (
    <div className="grid grid-cols-3 grid-rows-2 gap-px w-full h-full">
      {images.slice(0, 6).map((img, idx) => (
        <GridCell key={idx} media={img} />
      ))}
    </div>
  );
}

function GridCell({ media }: { media: (MediaRow & { is_user_media?: boolean }) | null }) {
  const enc = useEncryption();
  const isCatalog = !media?.is_user_media && !media?.is_e2e_encrypted;
  const { state, url } = useCatalogOrBlob(
    (media ?? {}) as MediaRow,
    isCatalog,
    enc.status === 'ready',
    enc.decrypt,
    !!media,
    true
  );

  return (
    <div className="relative size-full overflow-hidden bg-tint/10">
      {media && state === 'ready' && url ? (
        <img src={url} alt="" className="size-full object-cover" />
      ) : media && state === 'loading' ? (
        <div className="flex size-full items-center justify-center">
          <span className="w-5 h-5 animate-pulse rounded-full bg-grey-soft" />
        </div>
      ) : (
        <div className="size-full bg-gradient-to-br from-tint/50 via-grey-soft/5 to-surface/10 flex items-center justify-center">
          <span className="opacity-20">
            <Icon name="photos" size={16} color="var(--color-ink-3)" />
          </span>
        </div>
      )}
    </div>
  );
}

function ProgressCircle({
  done,
  total,
  onClick,
}: {
  done: number;
  total: number;
  onClick: () => void;
}) {
  const percentage = total === 0 ? 100 : Math.round((done / total) * 100);
  
  const radius = 34;
  const strokeWidth = 4;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <button
      onClick={onClick}
      className="relative flex items-center justify-center w-20 h-20 rounded-full bg-surface/90 backdrop-blur-md shadow-[0_4px_16px_rgba(0,0,0,0.15)] border border-line/10 active:scale-95 transition-all hover:bg-surface cursor-pointer"
      aria-label={`Milestones progress: ${percentage}%`}
    >
      <svg className="absolute inset-0 w-20 h-20 -rotate-90">
        <circle
          cx="40"
          cy="40"
          r={radius}
          className="stroke-grey-soft/40 fill-none"
          strokeWidth={strokeWidth}
        />
        <circle
          cx="40"
          cy="40"
          r={radius}
          className="stroke-accent fill-none transition-all duration-500 ease-out"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </svg>
      <div className="z-2 flex flex-col items-center justify-center leading-none text-ink">
        <span className="font-serif text-[18px] font-bold">{done}</span>
        <span className="my-0.5 h-px w-5 bg-ink/30" />
        <span className="text-[12px] font-semibold text-ink-3">{total}</span>
      </div>
    </button>
  );
}

function WorkingOnCard({
  milestone,
  onOpen,
}: {
  milestone: BabyMilestoneRow;
  onOpen: () => void;
}) {
  const enc = useEncryption();
  const t = useT();

  const mediaRow = useMemo((): MediaRow | null => {
    if (milestone.user_media_id) {
      return {
        id: milestone.user_media_id,
        media_type: milestone.user_media_type ?? 'image',
        mime_type: milestone.user_media_mime ?? 'image/jpeg',
        is_e2e_encrypted: !!milestone.user_media_encrypted,
        width_px: null,
        height_px: null,
        file_size_bytes: null,
        created_at: '',
      };
    }
    const cat = milestone.media?.[0];
    if (cat) {
      return {
        id: cat.id,
        media_type: cat.media_type,
        mime_type: cat.mime_type,
        is_e2e_encrypted: cat.is_e2e_encrypted,
        width_px: cat.width_px,
        height_px: cat.height_px,
        file_size_bytes: cat.duration_seconds,
        created_at: '',
      };
    }
    return null;
  }, [milestone]);

  // Catalog illustration (no parent photo) → public cacheable URL, no blob fetch.
  const isCatalogMedia = !milestone.user_media_id && !!milestone.media?.length;
  const { state, url } = useCatalogOrBlob(
    (mediaRow ?? {}) as MediaRow,
    isCatalogMedia,
    enc.status === 'ready',
    enc.decrypt,
    !!mediaRow,
    true
  );

  const meta = CAT_ICONS[milestone.group_key] ?? { icon: 'journey', color: 'var(--color-ink-3)' };

  return (
    <button
      onClick={onOpen}
      className="w-[45%] shrink-0 snap-start flex flex-col text-left group cursor-pointer outline-none active:scale-[0.98] transition-transform"
    >
      <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden border border-line bg-surface mb-2">
        {mediaRow && state === 'ready' && url ? (
          <img src={url} alt="" className="size-full object-cover" />
        ) : mediaRow && state === 'loading' ? (
          <span className="size-full flex items-center justify-center bg-tint/5">
            <span className="w-5 h-5 animate-pulse rounded-full bg-grey-soft" />
          </span>
        ) : (
          <div
            className="size-full flex items-center justify-center text-3xl select-none"
            style={{ background: `${meta.color}14` }}
          >
            <Icon name={meta.icon as any} size={28} color={meta.color} />
          </div>
        )}
      </div>
      <span
        className="text-[10px] font-bold uppercase tracking-wider block truncate"
        style={{ color: meta.color }}
      >
        {t('milestones.group.' + milestone.group_key, CAT_LABEL[milestone.group_key] ?? milestone.group_key)}
      </span>
      <h4 className="font-serif text-[14px] leading-tight tracking-tight text-ink font-semibold line-clamp-2 mt-0.5 group-hover:underline">
        {milestone.name}
      </h4>
    </button>
  );
}

interface CarouselProps {
  items: BabyMilestoneRow[];
  dobIso?: string;
  pendingId: number | null;
  onOpen: (id: number) => void;
  onSeen?: (id: number) => void;
  onDismiss: (id: number) => void;
}

function UpNextCarousel({ items, dobIso, pendingId, onOpen, onSeen, onDismiss }: CarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  const onScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== active) setActive(i);
  };

  return (
    <div>
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="scrollbar-none flex snap-x snap-mandatory gap-3 overflow-x-auto"
      >
        {items.map((r) => (
          <UpNextCard
            key={r.milestone_id}
            item={r}
            dobIso={dobIso}
            pending={pendingId === r.milestone_id}
            onOpen={onOpen}
            onSeen={onSeen}
            onDismiss={onDismiss}
          />
        ))}
      </div>

      {items.length > 1 && (
        <div className="mt-3 flex justify-center gap-1.5">
          {items.map((r, i) => (
            <span
              key={r.milestone_id}
              className={[
                'h-1.5 rounded-full transition-all',
                i === active ? 'w-4 bg-accent' : 'w-1.5 bg-grey-soft',
              ].join(' ')}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function UpNextCard({
  item,
  dobIso,
  pending,
  onOpen,
  onSeen,
  onDismiss,
}: {
  item: BabyMilestoneRow;
  dobIso?: string;
  pending: boolean;
  onOpen: (id: number) => void;
  onSeen?: (id: number) => void;
  onDismiss: (id: number) => void;
}) {
  const enc = useEncryption();
  const t = useT();
  const r = item;

  const mediaRow: MediaRow | null = r.user_media_id
    ? {
        id: r.user_media_id,
        media_type: r.user_media_type ?? 'image',
        mime_type: r.user_media_mime ?? 'image/jpeg',
        is_e2e_encrypted: !!r.user_media_encrypted,
        width_px: null,
        height_px: null,
        file_size_bytes: null,
        created_at: '',
      }
    : r.media?.[0]
    ? {
        id: r.media[0].id,
        media_type: r.media[0].media_type,
        mime_type: r.media[0].mime_type,
        is_e2e_encrypted: r.media[0].is_e2e_encrypted,
        width_px: r.media[0].width_px,
        height_px: r.media[0].height_px,
        file_size_bytes: r.media[0].duration_seconds,
        created_at: '',
      }
    : null;

  const isCatalogMedia = !r.user_media_id && !!r.media?.length;
  const { url } = useCatalogOrBlob(
    (mediaRow ?? {}) as MediaRow,
    isCatalogMedia,
    enc.status === 'ready',
    enc.decrypt,
    !!mediaRow,
    true
  );

  const meta = CAT_ICONS[r.group_key] ?? { icon: 'journey', color: 'var(--color-ink-3)' };

  return (
    <article
      className="relative w-full shrink-0 snap-center overflow-hidden rounded-2xl border bg-surface p-4 flex flex-col justify-between"
      style={{
        borderColor: 'var(--color-line)',
      }}
    >
      <button
        onClick={() => onOpen(r.milestone_id)}
        className="flex items-stretch w-full text-left min-h-[90px]"
      >
        {/* Left 1/3: Thumbnail or Icon */}
        <div
          className="w-1/3 relative shrink-0 overflow-hidden rounded-xl flex items-center justify-center mr-3.5"
          style={{ background: url ? 'var(--color-tint)' : `${meta.color}14` }}
        >
          {url ? (
            <img src={url} alt="" className="absolute inset-0 size-full object-cover object-center" />
          ) : (
            <Icon name={meta.icon as any} size={28} color={meta.color} />
          )}
        </div>

        {/* Right 2/3: Metadata and Text */}
        <div className="w-2/3 flex flex-col justify-between min-w-0 py-0.5">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-[0.15em]" style={{ color: meta.color }}>
              {t('milestones.group.' + r.group_key, CAT_LABEL[r.group_key] ?? r.group_key)} · {t('today.upNext.typicallyNow', 'typically now')}
            </span>
            <h4 className="mt-0.5 font-serif text-[16.5px] leading-snug tracking-tight text-ink font-semibold truncate">
              {r.name}
            </h4>
            <p className="mt-0.5 text-[12.5px] leading-snug text-ink-2 line-clamp-2">
              {r.description}
            </p>
          </div>
          <p className="mt-1.5 text-[10.5px] tabular-nums text-ink-3">
            {fmtRange(r.avg_start_weeks, r.median_achieved_weeks, r.median_achieved_weeks, dobIso)}
          </p>
        </div>
      </button>

      {onSeen && (
        <div className="mt-3.5 border-t border-hairline pt-3.5 flex gap-2 w-full">
          <button
            onClick={() => onDismiss(r.milestone_id)}
            disabled={pending}
            className="flex-1 rounded-full border border-line bg-surface py-2 text-[12.5px] font-semibold text-ink-2 active:scale-98 transition-transform disabled:opacity-50 cursor-pointer"
          >
            {t('today.upNext.notYet', 'Not yet')}
          </button>
          <button
            onClick={() => onSeen(r.milestone_id)}
            disabled={pending}
            className="flex-1 rounded-full bg-ink py-2 text-[12.5px] font-semibold text-bg active:scale-98 transition-transform disabled:opacity-60 cursor-pointer"
          >
            {pending ? t('common.saving', 'Saving…') : t('today.upNext.iSawThis', 'I saw this!')}
          </button>
        </div>
      )}
    </article>
  );
}

function ActivityAirbnbCard({
  activity,
  onClick,
  babyId,
}: {
  activity: ActivitySummary;
  onClick: () => void;
  babyId?: string | null;
}) {
  const t = useT();
  const enc = useEncryption();
  const setState = useSetActivityState();
  const isFavorite = !!activity.is_favorite;

  const toggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!babyId) return;
    setState.mutate({
      babyId,
      activityId: activity.id,
      is_favorite: !isFavorite,
    });
  };

  const primary = useMemo(() => {
    if (!activity.media) return null;
    const nonAudioMedia = activity.media.filter(
      (m) => m.media_type !== 'audio' && m.variant !== 'song_full' && m.variant !== 'song_instrumental' && m.variant !== 'audio'
    );
    return nonAudioMedia[0] || null;
  }, [activity.media]);

  const { state, url } = useCatalogOrBlob(
    (primary ?? { id: '', mime_type: '', is_e2e_encrypted: false }) as unknown as MediaRow,
    true, // activity.media is editorial catalog → public cacheable URL
    enc.status === 'ready',
    enc.decrypt,
    !!primary,
    true,
  );

  const categoryMeta = ACTIVITY_CATEGORY_META[activity.category] || { label: activity.category, color: '#D98E5A' };
  const categoryLabel = t('activities.category.' + activity.category, categoryMeta.label);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="w-[45%] shrink-0 snap-start flex flex-col text-left group cursor-pointer outline-none"
    >
      <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden border border-line bg-surface mb-2.5">
        {primary && state === 'ready' && url ? (
          <img src={url} alt={activity.title} className="size-full object-cover transition-transform group-active:scale-95 duration-200" />
        ) : primary && state === 'loading' ? (
          <span className="size-full flex items-center justify-center">
            <span className="w-5 h-5 animate-pulse rounded-full bg-grey-soft" />
          </span>
        ) : (
          <div
            className="size-full flex items-center justify-center text-3xl select-none"
            style={{ background: `${categoryMeta.color}14` }}
          >
            {activity.category === 'play' ? '🧸' : activity.category === 'sing' ? '🎤' : activity.category === 'music' ? '🎵' : activity.category === 'read' ? '📚' : '🗺️'}
          </div>
        )}

        {babyId && (
          <button
            onClick={toggleFavorite}
            className={[
              'absolute top-2 right-2 z-15 size-8 flex items-center justify-center rounded-full backdrop-blur-md transition-all shadow-xs outline-none cursor-pointer',
              isFavorite
                ? 'bg-accent-soft text-accent border border-accent/20'
                : 'bg-white/80 text-ink-3 hover:text-ink-2 border border-line/10',
            ].join(' ')}
          >
            <HeartIcon filled={isFavorite} size={13} />
          </button>
        )}
      </div>
      <span className="text-[10px] font-bold uppercase tracking-wider block" style={{ color: categoryMeta.color }}>
        {categoryLabel}
      </span>
      <h4 className="font-serif text-[14px] leading-tight tracking-tight text-ink font-semibold line-clamp-2 mt-0.5 group-hover:underline">
        {activity.title}
      </h4>
    </div>
  );
}

function HeartIcon({ filled, size = 18, className }: { filled: boolean; size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
    </svg>
  );
}
